import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  Clock3,
  Loader2,
  MessageCircle,
} from 'lucide-react';
import type { BuildIntake } from '../lib/buildQuote/types';
import {
  CONSULTATION_FOUNDER,
  CONSULTATION_FOUNDER_TITLE,
  CONSULTATION_STRATEGY_PACKAGES,
  DEFAULT_CONSULTATION_STRATEGY_MINUTES,
  getConsultationStrategyPackage,
  type ConsultationStrategyMinutes,
} from '../lib/buildQuote/types';
import { fetchBuildQuote, saveBuildQuote } from '../lib/buildQuote/api';
import { formatUsd } from '../lib/buildQuote/format';
import { getBuildIntakeErrors } from '../lib/buildQuote/validateIntake';
import AeoPageHead from '../components/AeoPageHead';

const INPUT_CLASS =
  'mt-2 w-full rounded-xl border border-white/15 bg-black/60 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none';

const INCLUDES = [
  '1-on-1 video session with Milan Cheeks',
  'Strategy on AI, agents, automation, robotics, growth, and product',
  'Clear next steps and recommended SELF Labs paths',
  'Stripe checkout — pay the full session amount',
];

const TOPICS = [
  'Living Website pricing and fit for your business',
  'Custom app / AI agent scope before a full Build quote',
  'Getting more from AI tools — ChatGPT, Claude, and similar systems',
  'Building AI workflows, agents, and everyday automation for your team',
  'SELF Phone, Resonatia, robotics, or Role Model AI',
  'Humanoid and robot strategy — pilots, ops, and product fit',
  'Quantum computing: when it matters and how SELF Labs thinks about it',
  'Incubator vs build vs partnership paths',
  'Fundraising narrative and product-market story',
];

function emptyConsultationIntake(minutes: ConsultationStrategyMinutes): BuildIntake {
  return {
    projectType: 'consultation',
    projectName: 'Strategy session',
    company: '',
    contactName: '',
    email: '',
    description: '',
    platforms: ['web'],
    scopeSize: 'small',
    needsDesign: false,
    needsAuth: false,
    needsPayments: false,
    needsAI: false,
    needsAdminDashboard: false,
    timeline: 'standard',
    consultationHours: 0,
    consultationMinutes: minutes,
  };
}

export default function ConsultationPage() {
  const navigate = useNavigate();
  const [minutes, setMinutes] = useState<ConsultationStrategyMinutes>(
    DEFAULT_CONSULTATION_STRATEGY_MINUTES
  );
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPackage = useMemo(
    () => getConsultationStrategyPackage(minutes)!,
    [minutes]
  );
  const totalCents = selectedPackage.priceCents;

  const previewLabel = useMemo(
    () => `${selectedPackage.label} · ${formatUsd(totalCents)}`,
    [selectedPackage, totalCents]
  );

  const handleBook = async () => {
    const intake: BuildIntake = {
      ...emptyConsultationIntake(minutes),
      contactName: contactName.trim(),
      email: email.trim(),
      company: company.trim(),
      description:
        description.trim() ||
        `Strategy session — ${selectedPackage.label} with ${CONSULTATION_FOUNDER} on AI strategy, agents, automation, robotics, and product.`,
      projectName: company.trim()
        ? `${company.trim()} — strategy session`
        : 'Strategy session',
    };

    const intakeErrors = getBuildIntakeErrors(intake);
    if (intakeErrors.length > 0) {
      setError(intakeErrors.join(' · '));
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const result = await fetchBuildQuote(intake, 0);
      const saved = await saveBuildQuote(result.intake ?? intake, result.quote);
      navigate(`/build/quote/${saved.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start booking');
      setLoading(false);
    }
  };

  return (
    <div
      id="consultation"
      data-avatar-target="consultation"
      className="min-h-screen bg-black text-white"
    >
      <AeoPageHead path="/consultation" />
      <Helmet>
        <title>Book Consultation — SELF Labs</title>
        <meta
          name="description"
          content={`Book a strategy session with ${CONSULTATION_FOUNDER} — 30 min $75, 60 min $200, or 90 min $300. Pay in full via Stripe.`}
        />
      </Helmet>

      <div
        className="pointer-events-none fixed inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 80%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 90% 70%, var(--glow-blue) 0%, transparent 45%)',
        }}
      />

      <div className="relative px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/products"
              className="inline-flex items-center text-sm text-white/50 transition-colors hover:text-white group"
            >
              <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
              Back to Products
            </Link>
          </motion.div>

          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-10 text-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-violet-400/30 bg-violet-500/10 px-4 py-1.5 text-xs font-medium text-violet-100/90">
              <CalendarDays className="h-3.5 w-3.5" />
              Strategy sessions · from $75
            </span>
            <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
              Book a consultation
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-white/60">
              Work directly with {CONSULTATION_FOUNDER}, {CONSULTATION_FOUNDER_TITLE}, on AI strategy,
              agent systems, startup growth, automation, robotics, and product development.
            </p>
          </motion.section>

          <div className="mt-14 grid gap-10 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.12 }}
              className="space-y-8"
              data-avatar-content="consultation-detail"
              aria-label="Consultation details"
            >
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-violet-400/30 bg-violet-500/10">
                    <MessageCircle className="h-5 w-5 text-violet-200" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-violet-200/80">
                      With {CONSULTATION_FOUNDER}
                    </p>
                    <h2 className="text-xl font-semibold">{CONSULTATION_FOUNDER_TITLE}</h2>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/55">
                  Use this when you want direct founder time — not a full software build yet. For
                  scoped apps and agents, use{' '}
                  <Link to="/build" className="text-white/80 underline hover:text-white">
                    Build with SELF Labs
                  </Link>
                  .
                </p>
                <ul className="mt-6 space-y-3">
                  {INCLUDES.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-white/65">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-violet-300" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                <h2 className="text-lg font-semibold">Good topics</h2>
                <ul className="mt-4 space-y-2.5">
                  {TOPICS.map((topic) => (
                    <li key={topic} className="flex items-start gap-2 text-sm text-white/55">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-white/40" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.18 }}
              className="rounded-2xl border border-violet-400/25 bg-gradient-to-b from-violet-500/[0.08] via-white/[0.03] to-transparent p-6 sm:p-8"
            >
              <div>
                <div className="flex items-center gap-2 text-sm text-violet-100/90">
                  <Clock3 className="h-4 w-4" />
                  Book a Strategy Session
                </div>
                <p className="mt-3 text-sm leading-relaxed text-white/55">
                  Work directly with Milan, Founder of SELF Labs, on AI strategy, agent systems,
                  startup growth, automation, robotics, and product development.
                </p>
              </div>

              <fieldset className="mt-6 space-y-3">
                <legend className="sr-only">Session length</legend>
                {CONSULTATION_STRATEGY_PACKAGES.map((pkg) => {
                  const selected = minutes === pkg.minutes;
                  return (
                    <label
                      key={pkg.minutes}
                      className={`relative flex cursor-pointer items-center justify-between gap-4 rounded-xl border px-4 py-3.5 transition-colors ${
                        pkg.recommended
                          ? selected
                            ? 'border-violet-300/70 bg-violet-500/20 shadow-[0_0_28px_rgba(167,139,250,0.22)]'
                            : 'border-violet-400/45 bg-violet-500/[0.12] hover:border-violet-300/60'
                          : selected
                            ? 'border-white/35 bg-white/[0.07]'
                            : 'border-white/12 bg-black/30 hover:border-white/25'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="consultationMinutes"
                          value={pkg.minutes}
                          checked={selected}
                          onChange={() => setMinutes(pkg.minutes)}
                          className="h-4 w-4 accent-violet-300"
                          data-avatar-field="consultationMinutes"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-white">
                            {pkg.label}
                          </span>
                          {pkg.recommended && (
                            <span className="mt-0.5 inline-flex rounded-full border border-violet-300/40 bg-violet-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-100">
                              Recommended
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="text-base font-semibold text-white">
                        ${pkg.priceUsd}
                      </span>
                    </label>
                  );
                })}
              </fieldset>

              <div className="mt-4 rounded-xl border border-white/10 bg-black/40 px-4 py-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/50">Total due</span>
                  <span className="font-semibold text-white">{previewLabel}</span>
                </div>
                <p className="mt-2 text-xs text-white/45">
                  Strategy sessions are paid in full at checkout — no deposit option.
                </p>
              </div>

              <label className="mt-5 block text-sm">
                <span className="text-white/55">Your name</span>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Full name"
                  autoComplete="name"
                  data-avatar-field="contactName"
                />
              </label>

              <label className="mt-4 block text-sm">
                <span className="text-white/55">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="you@company.com"
                  autoComplete="email"
                  data-avatar-field="email"
                />
              </label>

              <label className="mt-4 block text-sm">
                <span className="text-white/55">Company (optional)</span>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Company name"
                  autoComplete="organization"
                  data-avatar-field="company"
                />
              </label>

              <label className="mt-4 block text-sm">
                <span className="text-white/55">What do you want to cover?</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className={`${INPUT_CLASS} resize-y`}
                  placeholder="Share goals, questions, or context (optional — we'll still book your session)."
                  data-avatar-field="description"
                />
              </label>

              {error && (
                <p className="mt-4 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={() => void handleBook()}
                disabled={loading}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-black transition-transform hover:scale-[1.01] disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating booking…
                  </>
                ) : (
                  <>
                    Continue to checkout
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <p className="mt-4 text-center text-xs text-white/40">
                You&apos;ll review the total and pay in full on the next page.
              </p>

              <p className="mt-3 text-center text-xs text-white/35">
                Prefer the full intake form?{' '}
                <Link
                  to="/build?type=consultation"
                  className="text-white/55 underline hover:text-white/80"
                >
                  Open consultation in Build
                </Link>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
